import * as React from "react";
import { Grid2, Typography } from "@mui/material";
import { useBatteryInfo, useBatteryStructure, useBatterySystem } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import { DigitalTwinTabContainer } from "../../../../Theme/DigitalTwinStyles";
import { useTranslation } from "react-i18next";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import BatteryHealthSidebarV2 from "./BatteryHealthSidebarV2";
import BatteryHealthChartSectionV2 from "./BatteryHealthChartSectionV2";
import BatteryLifecycleMetricsV2 from "./BatteryLifecycleMetricsV2";
import OrgNotificationBanner from "../../../../Components/OrgNotificationBanner/OrgNotificationBanner";
import { formatComputedDate } from "../../../../Helper/DatePicker/DateConverter";

// Depth-first check for whether a hierarchy tree contains any node at one of the given levels.
export const treeHasAnyLevel = (nodes, levels) =>
    (nodes || []).some(
        (node) =>
            levels.includes(node.level?.toLowerCase()) ||
            treeHasAnyLevel(node.children, levels)
    );

const countMatchedLevels = (nodes, levels) =>
    levels.filter((level) => treeHasAnyLevel(nodes, [level])).length;

// Picks the hierarchy the sidebar should render. A battery can carry BOTH a
// BATTERY_SYSTEM tree (battery/string) and a BATTERY_STRUCTURE tree
// (pack/module/cell) — e.g. a string battery whose packs are also registered in
// packConfig — so the mere presence of one tree cannot decide the topology.
// Instead pick whichever tree actually covers more of the configured
// visibleBatteryParts, tie-breaking to BATTERY_STRUCTURE (pack topology).
export const resolveSidebarStructure = (batteryStructure, batterySystem, visibleBatteryParts) => {
    const levels = (visibleBatteryParts || []).map((v) => v.toLowerCase());
    const structureTree = batteryStructure || [];
    // Drop the battery root when the battery level is not shown, so strings become the top level.
    const systemTree = levels.includes('battery')
        ? (batterySystem || [])
        : (batterySystem || []).flatMap((battery) => battery.children || []);

    const structureMatches = countMatchedLevels(structureTree, levels);
    const systemMatches = countMatchedLevels(systemTree, levels);

    if (systemMatches > structureMatches) return systemTree;
    if (structureMatches > 0) return structureTree;
    return [];
};

export default function BatteryHealthV2({ batteryID }) {
    const { t } = useTranslation()

    const { batteryInfo } = useBatteryInfo()
    const batteryStructure = useBatteryStructure()
    const batterySystem = useBatterySystem()
    const pageContent = usePagesContent()

    const hasStringLevel = React.useMemo(
        () => treeHasAnyLevel(batterySystem, ['string']),
        [batterySystem]
    );

    // visibleBatteryParts from org meta controls which hierarchy levels show in the sidebar
    const batteryHealthConfig = pageContent?.metaInfo?.batteryHealth;
    const visibleBatteryParts = React.useMemo(
        () => batteryHealthConfig?.visibleBatteryParts || (hasStringLevel ? ["string", "pack"] : ["pack"]),
        [batteryHealthConfig, hasStringLevel]
    );

    // Full hierarchy tree passed to sidebar (sidebar filters by visibleBatteryParts internally)
    const sidebarStructure = React.useMemo(
        () => resolveSidebarStructure(batteryStructure, batterySystem, visibleBatteryParts),
        [batteryStructure, batterySystem, visibleBatteryParts]
    );

    const [selectedNode, setSelectedNode] = React.useState([]);

    // Battery-level "Last SOH Computed" date — a single per-entity scalar shown as
    // a caption near the degradation curve (not a per-point value on the chart).
    // Sourced from the Lifecycle Metrics table's existing real_time fetch (lifted
    // up via onBatterySohComputedDate) to avoid a duplicate identical API call.
    const [batterySohComputedDate, setBatterySohComputedDate] = React.useState(null);

    return (
        <DigitalTwinTabContainer>
            <Grid2 sx={{ paddingLeft: '12px', paddingRight: '12px', mb: 2 }}>
                <OrgNotificationBanner notifications={batteryHealthConfig?.notifications} />
            </Grid2>
            <Grid2 container flexDirection="column" gap="4px" paddingBottom={'20px'} paddingTop={'4px'} paddingLeft={'12px'}>
                <Typography variant="fw500s16lh20c000000">{t('SOH Degradation Curve')}</Typography>
                <Typography variant="fw400s12c686868">{t(`View how the SOH of your battery has changed with age or cycles completed`)}</Typography>
                {batterySohComputedDate && (
                    <Typography variant="fw400s12c686868" data-testid="battery-soh-computed-date-caption">
                        {t('Last SOH Computed')}: {formatComputedDate(batterySohComputedDate)}
                    </Typography>
                )}
            </Grid2>

            <Grid2 container sx={{
                borderRadius: '14px',
                border: '1px solid #E6E6E6',
                backgroundColor: '#FFF',
                height: '416px',
                flexWrap: 'nowrap',
                pr: 1.2,
            }}>
                <Grid2 sx={{ width: '20%', flexShrink: 0, borderRight: '1px solid #E6E6E6', zIndex: 1, mr: 1.2, overflow: 'hidden' }}>
                    <BatteryHealthSidebarV2
                        batteryStructure={sidebarStructure}
                        visibleBatteryParts={visibleBatteryParts}
                        onChangeNode={setSelectedNode}
                        maxScrollHeight="300px"
                    />
                </Grid2>

                <Grid2 flexGrow={1} sx={{ mr: 1.2 }}>
                    <BatteryHealthChartSectionV2
                        batteryID={batteryID}
                        selectedNode={selectedNode}
                        sidebarTree={sidebarStructure}
                    />
                </Grid2>
            </Grid2>

            <Grid2 container flexDirection="column" gap="4px" paddingTop={'20px'} paddingLeft={'12px'}>
                <Typography variant="fw500s16lh20c000000">{t(`Battery Lifecycle Metrics`)}</Typography>
            </Grid2>

            <Grid2 sx={{ mb: 10 }}>
                <BatteryLifecycleMetricsV2
                    batteryInfo={batteryInfo}
                    visibleBatteryParts={visibleBatteryParts}
                    batteryID={batteryID}
                    onBatterySohComputedDate={setBatterySohComputedDate}
                />
            </Grid2>
        </DigitalTwinTabContainer>
    );
}
